import React, { useEffect, useState } from 'react';
import { Shield, Loader2, History } from 'lucide-react';
import { api } from '@/lib/api';
import { ImpersonationRow } from '@/components/admin/RecentImpersonationsPanel';

/**
 * Read-only impersonation audit log for tenant administrators.
 *
 * Lists who impersonated whom in the current tenant (target user belongs to
 * the tenant), including sessions started by a platform super_admin. Each row
 * expands into the same forensic session timeline used on the super-admin home
 * panel — but with NO "Resume" action, so this stays a pure read-only log.
 *
 * The backend `/admin/impersonation-history` scopes results to the caller's
 * tenant automatically for non-super_admin roles.
 */
export function ImpersonationAuditLog() {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api.getImpersonationHistory(50)
      .then(s => { if (!cancelled) setSessions(s || []); })
      .catch(err => { if (!cancelled) setError(err.message || 'Failed to load impersonation log'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  if (loading) {
    return (
      <div className="glass-card rounded-2xl p-8 flex items-center justify-center" data-testid="impersonation-log-loading">
        <Loader2 className="w-5 h-5 text-muted-foreground animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="glass-card rounded-2xl p-6" data-testid="impersonation-log-error">
        <p className="text-xs text-destructive">Failed to load impersonation log: {error}</p>
      </div>
    );
  }

  if (sessions.length === 0) {
    return (
      <div className="glass-card rounded-2xl p-8 text-center" data-testid="impersonation-log-empty">
        <Shield className="w-8 h-8 text-muted-foreground/70 mx-auto mb-2" strokeWidth={1.5} />
        <p className="text-sm font-medium text-foreground">No impersonation activity</p>
        <p className="text-xs text-muted-foreground mt-1">
          When an admin views the app as another user in your tenant, it will be logged here.
        </p>
      </div>
    );
  }

  const visible = expanded ? sessions : sessions.slice(0, 10);

  return (
    <div className="glass-card rounded-2xl p-5" data-testid="impersonation-audit-log">
      <div className="flex items-center gap-2 mb-1">
        <Shield className="w-4 h-4 text-violet-400" />
        <h2 className="text-sm font-heading font-semibold text-foreground">Impersonation Log</h2>
        <span className="text-[10px] font-normal text-muted-foreground/70">({sessions.length})</span>
      </div>
      <p className="text-[11px] text-muted-foreground mb-3">
        A read-only record of who viewed the app as another user in your tenant. Expand a row to see exactly what happened during that session.
      </p>
      <div className="space-y-1">
        {visible.map(s => (
          <ImpersonationRow key={s.audit_id} session={s} />
        ))}
      </div>
      {sessions.length > 10 && (
        <button
          onClick={() => setExpanded(e => !e)}
          className="text-[10px] text-muted-foreground hover:text-foreground mt-3 flex items-center gap-1"
          data-testid="impersonation-log-toggle-expand"
        >
          <History className="w-3 h-3" /> {expanded ? 'Show less' : `Show all ${sessions.length}`}
        </button>
      )}
    </div>
  );
}
